import { Box, Flex, Text } from "@chakra-ui/layout";
import { Progress, Spinner, Icon } from "@chakra-ui/react";
import axios from "axios";
import JSZip from "jszip";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MdCheckCircle, MdError } from "react-icons/md";
import http from "infra/http";
import { useGrupoImpressaoContaIds } from "features/conta/hooks";
import colors from "resources/theme/colors";

type Status = "carregando" | "baixando" | "concluido" | "erro";

// Monta um nome de arquivo seguro a partir do nome da unidade, evitando
// caracteres inválidos e colisões entre boletos da mesma unidade.
function gerarNomeArquivo(
  nomeUnidade: string | undefined,
  index: number,
  nomesUsados: Map<string, number>
) {
  const base =
    (nomeUnidade ?? "")
      .trim()
      .replace(/[\\/:*?"<>|]/g, "-")
      .replace(/\s+/g, " ") || `boleto-${String(index + 1).padStart(3, "0")}`;

  const usos = nomesUsados.get(base) ?? 0;
  nomesUsados.set(base, usos + 1);

  return usos === 0 ? `${base}.pdf` : `${base} (${usos + 1}).pdf`;
}

function DownloadBoletos() {
  const { hash } = useParams<{ hash: string }>();

  const { data, isLoading, isError } = useGrupoImpressaoContaIds(hash);

  const [status, setStatus] = useState<Status>("carregando");
  const [baixados, setBaixados] = useState(0);
  const [total, setTotal] = useState(0);
  const [mensagemErro, setMensagemErro] = useState<string>();

  useEffect(() => {
    if (isError) {
      setStatus("erro");
      setMensagemErro("Não foi possível carregar a lista de boletos.");
    }
  }, [isError]);

  useEffect(() => {
    const contas = data?.contas;

    if (!contas?.length) {
      return;
    }

    // Permite cancelar o processo (e as requisições em andamento) caso o
    // componente seja desmontado — StrictMode e Fast Refresh remontam o
    // componente, o que dispararia o download novamente.
    let cancelado = false;
    const source = axios.CancelToken.source();

    const baixarTudo = async () => {
      setStatus("baixando");
      setTotal(contas.length);
      setBaixados(0);

      const zip = new JSZip();
      let falhas = 0;

      // Controla nomes repetidos (mais de um boleto por unidade).
      const nomesUsados = new Map<string, number>();

      for (let i = 0; i < contas.length; i++) {
        if (cancelado) return;

        const { contaId, unidade } = contas[i];

        try {
          const { data: blob } = await http.get(`/v1/boleto/${contaId}`, {
            responseType: "blob",
            cancelToken: source.token,
          });

          const nomeArquivo = gerarNomeArquivo(unidade?.nome, i, nomesUsados);

          zip.file(nomeArquivo, blob);
        } catch (err) {
          // Requisição abortada pelo cleanup: encerra silenciosamente.
          if (cancelado || axios.isCancel(err)) return;
          falhas++;
        }

        if (cancelado) return;
        setBaixados(i + 1);
      }

      if (cancelado) return;

      if (falhas === contas.length) {
        setStatus("erro");
        setMensagemErro("Não foi possível baixar nenhum boleto.");
        return;
      }

      const conteudoZip = await zip.generateAsync({ type: "blob" });

      if (cancelado) return;

      const url = window.URL.createObjectURL(conteudoZip);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `boletos-${hash}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      // Adia o revoke para não cancelar o download em alguns navegadores.
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      setStatus("concluido");
    };

    baixarTudo();

    return () => {
      cancelado = true;
      source.cancel();
    };
  }, [data, hash]);

  const carregando = isLoading || status === "carregando";
  const progresso = total > 0 ? Math.round((baixados / total) * 100) : 0;

  return (
    <Flex
      align="center"
      justify="center"
      minH="100vh"
      bg="gray.50"
      p={4}
    >
      <Box
        bg="white"
        borderRadius="lg"
        boxShadow="md"
        p={10}
        maxW="480px"
        w="100%"
        textAlign="center"
      >
        {status === "concluido" ? (
          <Icon as={MdCheckCircle} boxSize={12} color="green.400" mb={4} />
        ) : status === "erro" ? (
          <Icon as={MdError} boxSize={12} color="red.400" mb={4} />
        ) : (
          <Spinner
            thickness="4px"
            speed="0.65s"
            color={colors.primary}
            size="xl"
            mb={4}
          />
        )}

        <Text fontSize="xl" fontWeight="bold" mb={2}>
          {status === "concluido"
            ? "Download concluído!"
            : status === "erro"
            ? "Algo deu errado"
            : "Baixando seus arquivos"}
        </Text>

        {status === "erro" ? (
          <Text color="gray.600">{mensagemErro}</Text>
        ) : carregando ? (
          <Text color="gray.600">Preparando os boletos...</Text>
        ) : (
          <>
            <Text color="gray.600" mb={1}>
              {baixados} de {total} arquivos baixados
            </Text>

            <Box mt={4} mb={2}>
              <Progress
                value={progresso}
                size="sm"
                borderRadius="full"
                colorScheme="green"
                hasStripe={status === "baixando"}
                isAnimated={status === "baixando"}
              />
            </Box>

            {status === "baixando" ? (
              <Text fontSize="sm" color="gray.400">
                Isso pode levar alguns minutos. Não feche esta aba.
              </Text>
            ) : (
              <Text fontSize="sm" color="gray.400">
                O download do arquivo .zip foi iniciado automaticamente.
              </Text>
            )}
          </>
        )}
      </Box>
    </Flex>
  );
}

export default DownloadBoletos;
